
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ShoppingBag, Tag, Store, Globe, User, AlertTriangle } from 'lucide-react';

interface PaymentIntegrationsTabProps {
  onSaved: () => void;
}

const PaymentIntegrationsTab: React.FC<PaymentIntegrationsTabProps> = ({ onSaved }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('shopify');
  
  const [shopifyUrl, setShopifyUrl] = useState('');
  const [shopifyApiKey, setShopifyApiKey] = useState('');
  const [shopifyApiSecret, setShopifyApiSecret] = useState('');
  const [shopifyConnected, setShopifyConnected] = useState(false);
  
  const [woocommerceUrl, setWoocommerceUrl] = useState('');
  const [woocommerceKey, setWoocommerceKey] = useState('');
  const [woocommerceSecret, setWoocommerceSecret] = useState('');
  const [woocommerceConnected, setWoocommerceConnected] = useState(false);
  
  const [bigcommerceUrl, setBigcommerceUrl] = useState('');
  const [bigcommerceToken, setBigcommerceToken] = useState('');
  const [bigcommerceConnected, setBigcommerceConnected] = useState(false);
  
  const [autoApprove, setAutoApprove] = useState(false);
  
  // Load saved integration settings from localStorage on component mount
  React.useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem('ecommerceIntegrationSettings');
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          
          // Shopify
          setShopifyUrl(settings.shopify?.url || '');
          setShopifyApiKey(settings.shopify?.apiKey || '');
          setShopifyApiSecret(settings.shopify?.apiSecret || '');
          setShopifyConnected(settings.shopify?.connected || false);
          
          // WooCommerce
          setWoocommerceUrl(settings.woocommerce?.url || '');
          setWoocommerceKey(settings.woocommerce?.key || '');
          setWoocommerceSecret(settings.woocommerce?.secret || '');
          setWoocommerceConnected(settings.woocommerce?.connected || false);
          
          // BigCommerce
          setBigcommerceUrl(settings.bigcommerce?.url || '');
          setBigcommerceToken(settings.bigcommerce?.token || '');
          setBigcommerceConnected(settings.bigcommerce?.connected || false);
          
          // General settings
          setAutoApprove(settings.autoApprove || false);
        }
      } catch (error) {
        console.error('Error loading integration settings:', error);
      }
    };
    
    loadSettings();
  }, []);
  
  const saveSettings = (platform: string) => {
    try {
      const currentSettings = localStorage.getItem('ecommerceIntegrationSettings');
      const settings = currentSettings ? JSON.parse(currentSettings) : {};
      
      if (platform === 'shopify') {
        settings.shopify = {
          url: shopifyUrl,
          apiKey: shopifyApiKey,
          apiSecret: shopifyApiSecret,
          connected: shopifyConnected
        };
      } else if (platform === 'woocommerce') {
        settings.woocommerce = {
          url: woocommerceUrl,
          key: woocommerceKey,
          secret: woocommerceSecret,
          connected: woocommerceConnected
        };
      } else if (platform === 'bigcommerce') {
        settings.bigcommerce = {
          url: bigcommerceUrl,
          token: bigcommerceToken,
          connected: bigcommerceConnected
        };
      } else if (platform === 'general') {
        settings.autoApprove = autoApprove;
      }
      
      localStorage.setItem('ecommerceIntegrationSettings', JSON.stringify(settings));
      onSaved();
      
      toast({
        title: "Settings saved",
        description: `${platform.charAt(0).toUpperCase() + platform.slice(1)} integration settings saved successfully.`,
      });
    } catch (error) {
      console.error('Error saving integration settings:', error);
      toast({
        title: "Error",
        description: "Failed to save integration settings.",
        variant: "destructive"
      });
    }
  };
  
  const connectShopify = () => {
    if (!shopifyUrl || !shopifyApiKey || !shopifyApiSecret) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields for Shopify integration.",
        variant: "destructive"
      });
      return;
    }
    
    // Mock API connection
    setTimeout(() => {
      setShopifyConnected(true);
      saveSettings('shopify');
      toast({
        title: "Connected",
        description: "Successfully connected to Shopify store.",
      });
    }, 1000);
  };
  
  const connectWoocommerce = () => {
    if (!woocommerceUrl || !woocommerceKey || !woocommerceSecret) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields for WooCommerce integration.",
        variant: "destructive"
      });
      return;
    }
    
    // Mock API connection
    setTimeout(() => {
      setWoocommerceConnected(true);
      saveSettings('woocommerce');
      toast({
        title: "Connected",
        description: "Successfully connected to WooCommerce store.",
      });
    }, 1000);
  };
  
  const connectBigcommerce = () => {
    if (!bigcommerceUrl || !bigcommerceToken) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields for BigCommerce integration.",
        variant: "destructive"
      });
      return;
    }
    
    // Mock API connection
    setTimeout(() => {
      setBigcommerceConnected(true);
      saveSettings('bigcommerce');
      toast({
        title: "Connected",
        description: "Successfully connected to BigCommerce store.",
      });
    }, 1000);
  };
  
  const disconnectIntegration = (platform: string) => {
    if (platform === 'shopify') {
      setShopifyConnected(false);
    } else if (platform === 'woocommerce') {
      setWoocommerceConnected(false);
    } else if (platform === 'bigcommerce') {
      setBigcommerceConnected(false);
    }
    
    saveSettings(platform);
    toast({
      title: "Disconnected",
      description: `Successfully disconnected from ${platform.charAt(0).toUpperCase() + platform.slice(1)}.`,
    });
  };
  
  const saveGeneralSettings = () => {
    saveSettings('general');
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">E-Commerce Integrations</h3>
          <p className="text-sm text-muted-foreground">
            Connect your e-commerce platforms to automatically add new customers to your referral program.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Label htmlFor="auto-approve">Auto approve new users</Label>
          <Switch 
            id="auto-approve"
            checked={autoApprove}
            onCheckedChange={(checked) => {
              setAutoApprove(checked);
              saveGeneralSettings();
            }}
          />
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="shopify" className="flex items-center space-x-2">
            <ShoppingBag className="h-4 w-4" />
            <span>Shopify</span>
          </TabsTrigger>
          <TabsTrigger value="woocommerce" className="flex items-center space-x-2">
            <Store className="h-4 w-4" />
            <span>WooCommerce</span>
          </TabsTrigger>
          <TabsTrigger value="bigcommerce" className="flex items-center space-x-2">
            <Tag className="h-4 w-4" />
            <span>BigCommerce</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="shopify">
          <Card>
            <CardHeader>
              <CardTitle>Shopify Integration</CardTitle>
              <CardDescription>
                Connect your Shopify store to automatically add new customers to your referral program.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="shopify-url">Store URL</Label>
                <Input 
                  id="shopify-url" 
                  placeholder="https://your-store.myshopify.com" 
                  value={shopifyUrl}
                  onChange={(e) => setShopifyUrl(e.target.value)}
                  disabled={shopifyConnected}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shopify-api-key">API Key</Label>
                <Input 
                  id="shopify-api-key" 
                  placeholder="Your Shopify API Key" 
                  value={shopifyApiKey}
                  onChange={(e) => setShopifyApiKey(e.target.value)}
                  disabled={shopifyConnected}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shopify-api-secret">API Secret</Label>
                <Input 
                  id="shopify-api-secret"
                  type="password"
                  placeholder="Your Shopify API Secret" 
                  value={shopifyApiSecret}
                  onChange={(e) => setShopifyApiSecret(e.target.value)}
                  disabled={shopifyConnected}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              {shopifyConnected ? (
                <div className="w-full flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-green-500 mr-2"></div>
                    <span className="text-sm text-green-600">Connected</span>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => disconnectIntegration('shopify')}
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <Button onClick={connectShopify} className="w-full">
                  Connect to Shopify
                </Button>
              )}
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="woocommerce">
          <Card>
            <CardHeader>
              <CardTitle>WooCommerce Integration</CardTitle>
              <CardDescription>
                Connect your WooCommerce store to automatically add new customers to your referral program.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="woocommerce-url">Store URL</Label>
                <Input 
                  id="woocommerce-url" 
                  placeholder="https://your-wordpress-site.com" 
                  value={woocommerceUrl}
                  onChange={(e) => setWoocommerceUrl(e.target.value)}
                  disabled={woocommerceConnected}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="woocommerce-key">Consumer Key</Label>
                <Input 
                  id="woocommerce-key" 
                  placeholder="Your WooCommerce Consumer Key" 
                  value={woocommerceKey}
                  onChange={(e) => setWoocommerceKey(e.target.value)}
                  disabled={woocommerceConnected}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="woocommerce-secret">Consumer Secret</Label>
                <Input 
                  id="woocommerce-secret"
                  type="password"
                  placeholder="Your WooCommerce Consumer Secret" 
                  value={woocommerceSecret}
                  onChange={(e) => setWoocommerceSecret(e.target.value)}
                  disabled={woocommerceConnected}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              {woocommerceConnected ? (
                <div className="w-full flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-green-500 mr-2"></div>
                    <span className="text-sm text-green-600">Connected</span>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => disconnectIntegration('woocommerce')}
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <Button onClick={connectWoocommerce} className="w-full">
                  Connect to WooCommerce
                </Button>
              )}
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="bigcommerce">
          <Card>
            <CardHeader>
              <CardTitle>BigCommerce Integration</CardTitle>
              <CardDescription>
                Connect your BigCommerce store to automatically add new customers to your referral program.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bigcommerce-url">Store URL</Label>
                <Input 
                  id="bigcommerce-url" 
                  placeholder="https://your-store.bigcommerce.com" 
                  value={bigcommerceUrl}
                  onChange={(e) => setBigcommerceUrl(e.target.value)}
                  disabled={bigcommerceConnected}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bigcommerce-token">Access Token</Label>
                <Input 
                  id="bigcommerce-token"
                  type="password"
                  placeholder="Your BigCommerce Access Token" 
                  value={bigcommerceToken}
                  onChange={(e) => setBigcommerceToken(e.target.value)}
                  disabled={bigcommerceConnected}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              {bigcommerceConnected ? (
                <div className="w-full flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-green-500 mr-2"></div>
                    <span className="text-sm text-green-600">Connected</span>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => disconnectIntegration('bigcommerce')}
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <Button onClick={connectBigcommerce} className="w-full">
                  Connect to BigCommerce
                </Button>
              )}
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Auto-User Management
          </CardTitle>
          <CardDescription>
            Manage how users from connected e-commerce platforms are handled in your referral program.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <p className="text-sm">
                When enabled, new customers from your e-commerce platforms will be automatically added to your 
                referral program. They will receive a welcome email with an invitation to join.
              </p>
            </div>
            <div className="rounded-md bg-yellow-50 p-4 my-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Globe className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Available Integrations</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Shopify: {shopifyConnected ? 'Connected' : 'Not connected'}</li>
                      <li>WooCommerce: {woocommerceConnected ? 'Connected' : 'Not connected'}</li>
                      <li>BigCommerce: {bigcommerceConnected ? 'Connected' : 'Not connected'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Button 
                variant="secondary"
                onClick={() => window.location.href = '/admin/auto-users'}
              >
                Manage Pending Users
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentIntegrationsTab;
